import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CategoryIcon from '@mui/icons-material/Category';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
const SubCategoryModal = ({
  open,
  onClose,
  onSave,
  subCategoryData = null,
  mode = 'create', // 'create', 'edit', 'view'
  existingSubCategories = []
}) => {
  const { companyId, userId, token } = useAuth();
  const { logAudit } = useAuditLog();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  const [formData, setFormData] = useState({
    Name: '',
    CategoryId: ''
  });

  const [errors, setErrors] = useState({});
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');

  // Category dropdown state
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch Categories from API
  useEffect(() => {
    if (open && companyId && token) {
      fetchCategories();
    }
  }, [open, companyId, token]);

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await fetchData('CategoryApi', `/${companyId}`, token);
      if (response && response.response_data) {
        setCategories(response.response_data.filter(c => c.IsActive !== false));
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Populate form when modal opens
  useEffect(() => {
    if (open) {
      if (subCategoryData && mode !== 'create') {
        setFormData({
          Id: subCategoryData.Id || '',
          Name: subCategoryData.Name || '',
          CategoryId: subCategoryData.CategoryId || ''
        });
      } else {
        setFormData({ Name: '', CategoryId: '' });
      }
      setErrors({});
    }
  }, [open, subCategoryData, mode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.Name.trim()) {
      newErrors.Name = 'Sub-category name is required';
    }
    if (!formData.CategoryId) {
      newErrors.CategoryId = 'Category is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicate = () => {
    return existingSubCategories.find(sc => {
      if (mode === 'edit' && subCategoryData && sc.Id === subCategoryData.Id) return false;
      return (
        sc.Name?.trim().toLowerCase() === formData.Name.trim().toLowerCase() &&
        String(sc.CategoryId) === String(formData.CategoryId)
      );
    });
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const duplicate = checkDuplicate();
    if (duplicate) {
      setDuplicateMessage(
        `A sub-category named "${formData.Name}" already exists under the selected category.`
      );
      setDuplicateDialogOpen(true);
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        Name: formData.Name,
        CategoryId: parseInt(formData.CategoryId),
        CompanyId: companyId
      };

      if (mode === 'edit') {
        dataToSend.Id = subCategoryData.Id;
        dataToSend.ModifiedBy = userId;
        dataToSend.ModifiedDate = new Date();
        if (subCategoryData.CreatedBy) dataToSend.CreatedBy = subCategoryData.CreatedBy;
        if (subCategoryData.CreatedDate) dataToSend.CreatedDate = subCategoryData.CreatedDate;
        await updateData('SubCategoriesApi', subCategoryData.Id, dataToSend, token);
      logAudit(AUDIT_ACTION.UPDATE, 'SubCategoriesApi', subCategoryData.Id, { ...selectedItem }, { ...formData });
      } else {
        dataToSend.CreatedBy = userId;
        dataToSend.CreatedDate = new Date();
        await postData('SubCategoriesApi', '', dataToSend, token);
      logAudit(AUDIT_ACTION.CREATE, 'SubCategoriesApi', 0, null, { Name: formData.Name || '' });
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving sub-category:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'New Sub-Category';
      case 'edit':   return 'Edit Sub-Category';
      case 'view':   return 'Sub-Category Details';
      default:       return 'Sub-Category';
    }
  };

  const isReadOnly = mode === 'view';

  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          style: {
            borderRadius: isMobile ? '0' : '12px',
            background: 'linear-gradient(135deg, #1d2445ff 0%, #1b0b2bff 100%)',
            padding: '2px',
            margin: isMobile ? '0' : '8px'
          }
        }}
      >
        <Box style={{
          background: 'white',
          borderRadius: isMobile ? '0' : '10px',
          overflow: 'hidden',
          height: isMobile ? '100vh' : 'auto'
        }}>
          <DialogTitle style={{
            background: 'linear-gradient(135deg, #121627ff 0%, #2c1b3cff 100%)',
            color: 'white',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 'auto'
          }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <CategoryIcon style={{ fontSize: '18px' }} />
              <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>
                {getTitle()}
              </Typography>
            </Box>
            <IconButton onClick={onClose} style={{ color: 'white', padding: '2px' }} size="small">
              <CloseIcon style={{ fontSize: '18px' }} />
            </IconButton>
          </DialogTitle>

          <DialogContent style={{ padding: '8px 12px', paddingBottom: '4px' }}>
            <Typography variant="subtitle2" style={{
              color: '#666',
              marginBottom: '6px',
              fontSize: '10px',
              lineHeight: '1.2'
            }}>
              {mode === 'create' ? 'Add a new sub-category to your system' :
               mode === 'edit'   ? 'Update sub-category information' :
               'View sub-category details'}
            </Typography>

            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{
                  fontWeight: '600',
                  marginBottom: '3px',
                  color: '#2d3748',
                  fontSize: '12px'
                }}>
                  Sub-Category Information
                </Typography>
              </Grid>

              {/* Category Dropdown */}
              <Grid item xs={12}>
                <TextField
                  select
                  name="CategoryId"
                  label="Category *"
                  value={formData.CategoryId}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.CategoryId}
                  helperText={errors.CategoryId}
                  disabled={isReadOnly || categoriesLoading}
                  SelectProps={{ native: true }}
                  InputProps={{
                    style: inputStyle,
                    endAdornment: categoriesLoading ? <CircularProgress size={14} /> : null
                  }}
                  InputLabelProps={{ style: labelStyle, shrink: true }}
                  FormHelperTextProps={{ style: helperStyle }}
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.Id} value={cat.Id}>{cat.Name}</option>
                  ))}
                </TextField>
              </Grid>

              {/* Sub-Category Name */}
              <Grid item xs={12}>
                <TextField
                  name="Name"
                  label="Sub-Category Name *"
                  value={formData.Name}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.Name}
                  helperText={errors.Name}
                  disabled={isReadOnly}
                  placeholder="Enter sub-category name"
                  inputProps={{ maxLength: 100 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions style={{
            padding: '8px 12px',
            background: '#f8f9fa',
            borderTop: '1px solid #e9ecef',
            gap: '6px',
            minHeight: 'auto'
          }}>
            <Button
              onClick={onClose}
              style={{
                color: '#6c757d',
                textTransform: 'none',
                fontWeight: '500',
                fontSize: '12px',
                padding: '4px 8px'
              }}
            >
              Cancel
            </Button>
            {!isReadOnly && (
              <Button
                onClick={handleSave}
                disabled={loading}
                variant="contained"
                style={{
                  background: 'linear-gradient(135deg, #53dac3ff 0%, #45a359ff 100%)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: '600',
                  borderRadius: '4px',
                  padding: '4px 12px',
                  fontSize: '12px'
                }}
              >
                {loading ? 'Saving...' : mode === 'create' ? 'Add Sub-Category' : 'Update Sub-Category'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>

      {/* Duplicate Warning Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon style={{ fontSize: '64px', color: '#ff9800', marginBottom: '16px' }} />
          <Typography variant="h6" style={{
            fontWeight: '600',
            marginBottom: '12px',
            color: '#2d3748',
            fontSize: '16px'
          }}>
            Duplicate Sub-Category Found
          </Typography>
          <Typography style={{ color: '#666', fontSize: '14px', lineHeight: '1.5' }}>
            {duplicateMessage}
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
          <Button
            onClick={() => setDuplicateDialogOpen(false)}
            variant="contained"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              textTransform: 'none',
              fontWeight: '600',
              borderRadius: '8px',
              padding: '8px 32px',
              fontSize: '14px'
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubCategoryModal;
